/**
 * One keyboard/focus manager for stacked drawers and modals: Escape and Tab
 * always go to the top layer, and everything below it (the app root and lower
 * layers) is inert until it closes. Focus returns to whatever opened the layer.
 */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', 'details > summary', '[tabindex]:not([tabindex="-1"])',
].join(',');

interface Layer {
  element: HTMLElement;
  close: () => void;
  blocked: () => boolean;
  restore: Array<() => void>;
}

const layers: Layer[] = [];

const makeInert = (element: HTMLElement) => {
  const inert = element.getAttribute('inert');
  const hidden = element.getAttribute('aria-hidden');
  element.setAttribute('inert', '');
  element.setAttribute('aria-hidden', 'true');
  return () => {
    if (inert === null) element.removeAttribute('inert'); else element.setAttribute('inert', inert);
    if (hidden === null) element.removeAttribute('aria-hidden'); else element.setAttribute('aria-hidden', hidden);
  };
};

const focusables = (element: HTMLElement) =>
  Array.from(element.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((node) => !node.closest('[inert]'));

function onKeyDown(event: KeyboardEvent) {
  const top = layers[layers.length - 1];
  if (!top) return;
  if (event.key === 'Escape') {
    if (!top.blocked()) {
      event.preventDefault();
      top.close();
    }
    return;
  }
  if (event.key !== 'Tab') return;
  const items = focusables(top.element);
  if (!items.length) {
    event.preventDefault();
    top.element.focus();
    return;
  }
  const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const index = active ? items.indexOf(active) : -1;
  if (index === -1) {
    event.preventDefault();
    (event.shiftKey ? items[items.length - 1] : items[0]).focus();
  } else if (event.shiftKey && index === 0) {
    event.preventDefault();
    items[items.length - 1].focus();
  } else if (!event.shiftKey && index === items.length - 1) {
    event.preventDefault();
    items[0].focus();
  }
}

export function pushLayer(
  element: HTMLElement,
  initialFocus: HTMLElement | null,
  close: () => void,
  blocked: () => boolean,
): () => void {
  const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const below = [document.getElementById('root'), ...layers.map((layer) => layer.element)]
    .filter((node): node is HTMLElement => Boolean(node));
  const layer: Layer = { element, close, blocked, restore: below.map(makeInert) };
  if (!layers.length) document.addEventListener('keydown', onKeyDown);
  layers.push(layer);
  const timer = window.setTimeout(() => (initialFocus ?? element).focus(), 0);
  return () => {
    window.clearTimeout(timer);
    const index = layers.indexOf(layer);
    if (index >= 0) layers.splice(index, 1);
    layer.restore.forEach((undo) => undo());
    if (!layers.length) document.removeEventListener('keydown', onKeyDown);
    if (returnFocus && document.contains(returnFocus)) returnFocus.focus();
  };
}

export const layerCount = () => layers.length;
